import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField as MuiTextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  LinearProgress,
  Snackbar,
  Tooltip
} from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import Parse from 'parse';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import CustomPagination from '../../components/CustomPagination';

// Player Email ID is not stored in the play/round data, so it is not a
// searchable field. Search Type maps to fields the backend can actually match.
const SEARCH_TYPES = [
  { label: 'All', value: 'all' },
  { label: 'Spin ID', value: 'spinId' },
  { label: 'Player ID', value: 'playerId' },
  { label: 'Username', value: 'username' }
];

const formatGameName = (modeId) =>
  (modeId || '')
    .replace(/^RgsOutcome_/i, '')
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();

// RgsRound / PlayHistory store monetary values as decimal currency units
// (e.g. bet = 0.2, win = 2, balance = 6282868.8), NOT cents. Do not divide by 100.
const formatAmount = (val) => (val != null ? Number(val).toFixed(2) : '-');

const PlayerSpinSearch = () => {
  const [searchType, setSearchType] = useState('all');
  const [searchValue, setSearchValue] = useState('');
  // gameModeId = '' means "All Games" (no game filter applied).
  const [gameName, setGameName] = useState('');
  const [gameModeId, setGameModeId] = useState('');

  const [games, setGames] = useState([]);
  const [gamesLoading, setGamesLoading] = useState(true);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [total, setTotal] = useState(0);

  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [showExportSnackbar, setShowExportSnackbar] = useState(false);

  // Load games list
  useEffect(() => {
    const loadGames = async () => {
      setGamesLoading(true);
      try {
        const sessionToken = localStorage.getItem('sessionToken');
        const result = await Parse.Cloud.run('rgsListGamesForDropdown', {}, { sessionToken });
        if (result?.games) setGames(result.games);
      } catch {
        setGames([]);
      } finally {
        setGamesLoading(false);
      }
    };
    loadGames();
  }, []);

  const fetchData = async (customPage = 1, customPerPage = perPage) => {
    setErrorMsg('');
    setLoading(true);

    try {
      const sessionToken = localStorage.getItem('sessionToken');
      const operatorId = localStorage.getItem('operatorId');

      const searchParam =
        searchType !== 'all' && searchValue.trim() ? searchValue.trim() : undefined;

      const result = await Parse.Cloud.run(
        'rgsGetOperatorGameSpinAuditLog',
        {
          operatorId,
          // Omit gameModeId for "All Games"; the backend then returns every game.
          ...(gameModeId && { gameModeId }),
          page: customPage,
          limit: customPerPage,
          ...(searchParam && { search: searchParam, searchField: searchType })
        },
        { sessionToken }
      );

      setData(result.records || []);
      setTotal(result.total || 0);
    } catch (err) {
      console.error('Spin search error:', err);
      setErrorMsg(err.message || 'Failed to fetch spin records.');
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    setHasSearched(true);
    fetchData(1);
  };

  const handleClear = () => {
    setSearchType('all');
    setSearchValue('');
    setGameName('');
    setGameModeId('');
    setData([]);
    setTotal(0);
    setHasSearched(false);
    setErrorMsg('');
    setPage(1);
  };

  const handleExport = async () => {
    setIsExporting(true);
    setShowExportSnackbar(true);

    try {
      const sessionToken = localStorage.getItem('sessionToken');
      const operatorId = localStorage.getItem('operatorId');
      const searchParam =
        searchType !== 'all' && searchValue.trim() ? searchValue.trim() : undefined;

      const result = await Parse.Cloud.run(
        'rgsGetOperatorGameSpinAuditLog',
        {
          operatorId,
          ...(gameModeId && { gameModeId }),
          page: 1,
          limit: 10000,
          ...(searchParam && { search: searchParam, searchField: searchType })
        },
        { sessionToken }
      );

      const rows = result.records || [];

      const selectedFields = rows.map((row, i) => {
        const isWin = (row.winAmount || 0) > (row.betAmount || 0);
        const netWin = (row.winAmount || 0) - (row.betAmount || 0);
        setExportProgress(Math.floor((i / rows.length) * 80) + 10);
        return {
          Status: isWin ? 'Win' : 'Loss',
          'Game Name': row.gameName || formatGameName(row.modeName) || '-',
          'Game Type': row.gameType || '-',
          'Spin ID': row.id,
          'Session ID': row.spinId || '-',
          'Spin Time': row.dateTime || row.createdAt || '-',
          'Bet Amount': formatAmount(row.betAmount),
          'Win/Loss Amount': formatAmount(row.winAmount),
          'Net Win': formatAmount(netWin),
          Balance: formatAmount(row.balanceAfterSpin),
          Currency: row.currency || '-',
          'Win/Loss Time': row.dateTime || row.createdAt || '-'
        };
      });

      setExportProgress(95);
      const worksheet = XLSX.utils.json_to_sheet(selectedFields);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Spin Search Results');
      const xlsData = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      setExportProgress(100);
      saveAs(new Blob([xlsData], { type: 'application/octet-stream' }), 'SpinSearchResults.xlsx');
      setTimeout(() => setShowExportSnackbar(false), 800);
    } catch (err) {
      console.error('Export error:', err);
      alert('Export failed: ' + err.message);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      {/* Export Snackbar */}
      <Snackbar
        open={showExportSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        autoHideDuration={null}
      >
        <Alert severity="info" icon={false} sx={{ px: 2 }}>
          <Typography variant="body2" mb={0.5}>
            Exporting... {exportProgress}%
          </Typography>
          <LinearProgress
            variant="determinate"
            value={exportProgress}
            sx={{ width: 260, height: 6, borderRadius: 5 }}
          />
        </Alert>
      </Snackbar>

      {/* Filters */}
      <Box sx={{ backgroundColor: '#242424', borderRadius: 2, p: 2.5, mb: 2 }}>
        <Typography sx={{ color: '#FFC107', fontWeight: 600, fontSize: 15, mb: 2 }}>
          Search Filters
        </Typography>

        <Box display="flex" flexWrap="wrap" gap={2} alignItems="flex-end">
          {/* Search Type */}
          <FormControl size="small" sx={{ minWidth: 170, ...selectContainerSx }}>
            <InputLabel sx={{ color: 'rgba(255,255,255,0.6)' }}>Search Type</InputLabel>
            <Select
              label="Search Type"
              value={searchType}
              onChange={(e) => {
                setSearchType(e.target.value);
                setSearchValue('');
              }}
              sx={selectSx}
            >
              {SEARCH_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Search Value */}
          <MuiTextField
            size="small"
            label="Search Value"
            placeholder={
              searchType === 'spinId'
                ? 'Enter Spin ID'
                : searchType === 'playerId'
                  ? 'Enter Player ID'
                  : searchType === 'username'
                    ? 'Enter Username'
                    : 'Enter search value'
            }
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !loading && handleSearch()}
            disabled={searchType === 'all'}
            sx={{ ...fieldSx, minWidth: 220 }}
          />

          {/* Game — "All Games" plus every game assigned to the operator */}
          <FormControl size="small" sx={{ minWidth: 220, ...selectContainerSx }}>
            <InputLabel shrink sx={{ color: 'rgba(255,255,255,0.6)' }}>
              Game
            </InputLabel>
            <Select
              label="Game"
              notched
              value={gameName}
              displayEmpty
              disabled={gamesLoading}
              onChange={(e) => {
                const selected = games.find((g) => g.name === e.target.value);
                setGameName(e.target.value);
                setGameModeId(selected?.slug || '');
              }}
              sx={selectSx}
            >
              <MenuItem value="">All Games</MenuItem>
              {games.map((g) => (
                <MenuItem key={g.id} value={g.name}>
                  {g.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Tooltip title="" placement="top">
            <span>
              <Button variant="contained" onClick={handleSearch} disabled={loading} sx={applyBtnSx}>
                {loading ? <CircularProgress size={18} sx={{ color: '#000' }} /> : 'Search'}
              </Button>
            </span>
          </Tooltip>

          <Button variant="outlined" onClick={handleClear} disabled={loading} sx={clearBtnSx}>
            Clear
          </Button>

          <Button
            variant="contained"
            startIcon={<GetAppIcon />}
            onClick={handleExport}
            disabled={isExporting || !hasSearched || data.length === 0}
            sx={applyBtnSx}
          >
            Download
          </Button>
        </Box>

        {errorMsg && (
          <Alert severity="warning" sx={{ mt: 2 }} onClose={() => setErrorMsg('')}>
            {errorMsg}
          </Alert>
        )}
      </Box>

      {/* Results Table */}
      <Box sx={{ backgroundColor: '#242424', borderRadius: 2, p: 2 }}>
        <TableContainer component={Paper} sx={{ backgroundColor: '#1A1A1A', mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#2A2A2A' }}>
                {[
                  'Status',
                  'Game Name',
                  'Game Type',
                  'Spin ID',
                  'Session ID',
                  'Spin Time',
                  'Bet Amount',
                  'Win/Loss Amount',
                  'Net Win',
                  'Balance',
                  'Currency',
                  'Win/Loss Time'
                ].map((col) => (
                  <TableCell
                    key={col}
                    sx={{ color: '#FFC107', fontWeight: 600, py: 1.5, whiteSpace: 'nowrap' }}
                  >
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                    <CircularProgress sx={{ color: '#FFC107' }} />
                  </TableCell>
                </TableRow>
              ) : data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ py: 4, color: '#fff' }}>
                    {hasSearched
                      ? 'No spin records found for the given filters.'
                      : 'Select a Game Name and click Search to view spin records.'}
                  </TableCell>
                </TableRow>
              ) : (
                data.map((row) => {
                  const isWin = (row.winAmount || 0) > (row.betAmount || 0);
                  const netWin = (row.winAmount || 0) - (row.betAmount || 0);
                  return (
                    <TableRow key={row.id} sx={{ '&:hover': { backgroundColor: '#2A2A2A' } }}>
                      <TableCell>
                        <Chip
                          label={isWin ? 'Win' : 'Loss'}
                          size="small"
                          sx={{
                            backgroundColor: isWin
                              ? 'rgba(76,175,80,0.15)'
                              : 'rgba(244,67,54,0.15)',
                            color: isWin ? '#4caf50' : '#f44336',
                            fontWeight: 600,
                            fontSize: '11px',
                            height: 20
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: '#fff', fontSize: 12, whiteSpace: 'nowrap' }}>
                        {row.gameName || formatGameName(row.modeName) || '-'}
                      </TableCell>
                      <TableCell sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>
                        {row.gameType || '-'}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: 'rgba(255,255,255,0.6)',
                          fontSize: 11,
                          maxWidth: 110,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        <Tooltip title={row.id}>
                          <span>{row.id}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        sx={{
                          color: 'rgba(255,255,255,0.6)',
                          fontSize: 11,
                          maxWidth: 110,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        <Tooltip title={row.spinId || '-'}>
                          <span>{row.spinId || '-'}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ color: '#fff', fontSize: 12, whiteSpace: 'nowrap' }}>
                        {row.dateTime ||
                          (row.createdAt ? new Date(row.createdAt).toLocaleString() : '-')}
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#fff', fontSize: 12 }}>
                        {formatAmount(row.betAmount)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: isWin ? '#4caf50' : '#f44336', fontSize: 12 }}
                      >
                        {formatAmount(row.winAmount)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: netWin >= 0 ? '#4caf50' : '#f44336', fontSize: 12 }}
                      >
                        {formatAmount(netWin)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#fff', fontSize: 12 }}>
                        {formatAmount(row.balanceAfterSpin)}
                      </TableCell>
                      <TableCell sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>
                        {row.currency || '-'}
                      </TableCell>
                      <TableCell sx={{ color: '#fff', fontSize: 12, whiteSpace: 'nowrap' }}>
                        {row.dateTime ||
                          (row.createdAt ? new Date(row.createdAt).toLocaleString() : '-')}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {total > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CustomPagination
              page={page}
              perPage={perPage}
              total={total}
              setPage={(p) => {
                setPage(p);
                fetchData(p);
              }}
              setPerPage={(pp) => {
                setPerPage(pp);
                setPage(1);
                fetchData(1, pp);
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

const fieldSx = {
  '& .MuiOutlinedInput-root': { color: '#fff' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#FFC107' },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#FFC107'
  }
};

const selectContainerSx = {
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#FFC107' }
};

const selectSx = {
  color: '#fff',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#FFC107' },
  '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.6)' }
};

const applyBtnSx = {
  background: '#FFC107',
  color: '#0A0A0A',
  textTransform: 'none',
  fontWeight: 600,
  '&:hover': { backgroundColor: '#FFA000' },
  '&:disabled': { backgroundColor: 'rgba(255,193,7,0.3)', color: 'rgba(0,0,0,0.4)' }
};

const clearBtnSx = {
  borderColor: '#FFC107',
  color: '#FFC107',
  textTransform: 'none',
  '&:hover': { borderColor: '#FFA000', color: '#FFA000', backgroundColor: 'rgba(255,193,7,0.08)' }
};

export default PlayerSpinSearch;
